import React, { useEffect, useState } from 'react';
import { Users, Shield, Key, Activity, AlertTriangle, CheckCircle, ExternalLink, ChevronRight } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { LoginLocationsMap } from '../../components/LoginLocationsMap';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { useAdmin } from '../../contexts/AdminContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin`;

export const AdminDashboard = () => {
  const { getToken } = useAdmin();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogType, setDialogType] = useState(null);
  const [dialogData, setDialogData] = useState([]);
  const [loadingDialog, setLoadingDialog] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = async (type) => {
    // For Admin Accounts, navigate to the page
    if (type === 'admin_accounts') {
      navigate('/admin/admins');
      return;
    }

    setDialogType(type);
    setShowDialog(true);
    setLoadingDialog(true);
    
    try {
      let endpoint = '';
      switch (type) {
        case 'total_users':
        case 'active_users':
          endpoint = '/users';
          break;
        case 'total_tokens':
          endpoint = '/users'; // We'll process tokens from users
          break;
        case 'recent_logins':
          endpoint = '/logs?action=login&limit=50';
          break;
        case 'failed_logins':
          endpoint = '/logs?status=failure&limit=50';
          break;
        default:
          break;
      }

      const response = await axios.get(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });

      let processedData = response.data;

      // Process data based on type
      if (type === 'active_users') {
        processedData = response.data.filter(user => user.is_active);
      } else if (type === 'total_tokens') {
        // Flatten tokens from all users
        const tokens = [];
        response.data.forEach(user => {
          if (user.tokens && user.tokens.length > 0) {
            user.tokens.forEach(token => {
              tokens.push({
                ...token,
                username: user.username
              });
            });
          }
        });
        processedData = tokens;
      }

      setDialogData(processedData);
    } catch (error) {
      console.error('Error fetching drill-down data:', error);
      toast.error('Failed to load details');
    } finally {
      setLoadingDialog(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDialogTitle = () => {
    switch (dialogType) {
      case 'total_users': return 'All Users';
      case 'active_users': return 'Active Users';
      case 'total_tokens': return 'All Tokens';
      case 'recent_logins': return 'Recent Logins (24h)';
      case 'failed_logins': return 'Failed Login Attempts (24h)';
      default: return 'Details';
    }
  };

  const renderDialogContent = () => {
    if (loadingDialog) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      );
    }

    if (dialogData.length === 0) {
      return (
        <div className="text-center py-8 text-slate-500 font-ibm">
          <p>No data found</p>
        </div>
      );
    }

    // Render different content based on dialog type
    if (dialogType === 'total_users' || dialogType === 'active_users') {
      return (
        <div className="space-y-3">
          {dialogData.map((user) => (
            <div key={user.username} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-ibm font-semibold text-slate-900">{user.username}</h4>
                    {user.is_active && (
                      <Badge className="bg-green-100 text-green-800 text-xs">Active</Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 font-ibm mb-1">
                    <strong>Email:</strong> {user.email || 'N/A'}
                  </p>
                  <div className="flex gap-4 text-xs text-slate-500 font-ibm">
                    <span><strong>Tokens:</strong> {user.total_tokens || 0}</span>
                    <span><strong>Logins:</strong> {user.total_logins || 0}</span>
                    <span><strong>Last Login:</strong> {formatDate(user.last_login)}</span>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/admin/users')}
                  className="text-xs"
                >
                  <ExternalLink className="h-3 w-3 mr-1" />
                  View
                </Button>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (dialogType === 'total_tokens') {
      return (
        <div className="space-y-3">
          {dialogData.map((token, index) => (
            <div key={index} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-ibm font-semibold text-slate-900">{token.name}</h4>
                    <Badge className={`text-xs ${
                      token.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {token.status}
                    </Badge>
                    <Badge variant="outline" className="text-xs">{token.type}</Badge>
                  </div>
                  <p className="text-sm text-slate-600 font-ibm mb-1">
                    <strong>User:</strong> {token.username}
                  </p>
                  <p className="text-xs text-slate-500 font-ibm">
                    <strong>Created:</strong> {formatDate(token.created_at)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (dialogType === 'recent_logins' || dialogType === 'failed_logins') {
      return (
        <div className="space-y-3">
          {dialogData.map((log, index) => (
            <div key={index} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Badge className={`text-xs ${
                    log.status === 'success' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {log.status}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    {log.action.replace(/_/g, ' ')}
                  </Badge>
                </div>
                <span className="text-xs text-slate-500 font-ibm">{formatDate(log.timestamp)}</span>
              </div>
              <div className="text-sm text-slate-700 font-ibm mb-1">
                <strong>User:</strong> {log.admin_username || log.target}
              </div>
              <div className="text-sm text-slate-600 font-ibm">{log.details}</div>
              {log.region && (
                <div className="text-xs text-slate-500 font-ibm mt-2">
                  {log.region} {log.ip_address && `• ${log.ip_address}`}
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-outfit font-bold text-3xl text-foreground mb-2">{t('admin.dashboard.title')}</h1>
        <p className="text-muted-foreground font-ibm">{t('admin.dashboard.subtitle')}</p>
      </div>

      {/* Statistics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card 
          className="p-6 border border-border cursor-pointer hover:shadow-lg hover:scale-105 transition-all duration-200"
          onClick={() => handleCardClick('total_users')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-ibm text-muted-foreground mb-1">{t('admin.dashboard.total_users')}</p>
              <p className="text-3xl font-outfit font-bold text-foreground">{stats?.total_users || 0}</p>
            </div>
            <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-xs text-blue-600 font-ibm">
            Click to view details <ChevronRight className="h-3 w-3 ml-1" />
          </div>
        </Card>

        <Card 
          className="p-6 border border-border cursor-pointer hover:shadow-lg hover:scale-105 transition-all duration-200"
          onClick={() => handleCardClick('active_users')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-ibm text-muted-foreground mb-1">{t('admin.dashboard.active_users')}</p>
              <p className="text-3xl font-outfit font-bold text-foreground">{stats?.active_users || 0}</p>
            </div>
            <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-emerald-600" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-xs text-emerald-600 font-ibm">
            Click to view details <ChevronRight className="h-3 w-3 ml-1" />
          </div>
        </Card>

        <Card 
          className="p-6 border border-border cursor-pointer hover:shadow-lg hover:scale-105 transition-all duration-200"
          onClick={() => handleCardClick('total_tokens')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-ibm text-muted-foreground mb-1">{t('admin.dashboard.total_tokens')}</p>
              <p className="text-3xl font-outfit font-bold text-foreground">{stats?.total_tokens || 0}</p>
            </div>
            <div className="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center">
              <Key className="h-6 w-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-xs text-purple-600 font-ibm">
            Click to view details <ChevronRight className="h-3 w-3 ml-1" />
          </div>
        </Card>

        <Card 
          className="p-6 border border-border cursor-pointer hover:shadow-lg hover:scale-105 transition-all duration-200"
          onClick={() => handleCardClick('admin_accounts')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-ibm text-muted-foreground mb-1">{t('admin.dashboard.admin_accounts')}</p>
              <p className="text-3xl font-outfit font-bold text-foreground">{stats?.total_admins || 0}</p>
            </div>
            <div className="w-12 h-12 bg-slate-50 rounded-lg flex items-center justify-center">
              <Shield className="h-6 w-6 text-slate-600" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-xs text-slate-600 font-ibm">
            Click to view page <ChevronRight className="h-3 w-3 ml-1" />
          </div>
        </Card>

        <Card 
          className="p-6 border border-border cursor-pointer hover:shadow-lg hover:scale-105 transition-all duration-200"
          onClick={() => handleCardClick('recent_logins')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-ibm text-muted-foreground mb-1">{t('admin.dashboard.recent_logins')}</p>
              <p className="text-3xl font-outfit font-bold text-foreground">{stats?.recent_logins || 0}</p>
            </div>
            <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
              <Activity className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-xs text-green-600 font-ibm">
            Click to view details <ChevronRight className="h-3 w-3 ml-1" />
          </div>
        </Card>

        <Card 
          className="p-6 border border-border cursor-pointer hover:shadow-lg hover:scale-105 transition-all duration-200"
          onClick={() => handleCardClick('failed_logins')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-ibm text-muted-foreground mb-1">{t('admin.dashboard.failed_logins')}</p>
              <p className="text-3xl font-outfit font-bold text-foreground">{stats?.failed_logins || 0}</p>
            </div>
            <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-xs text-red-600 font-ibm">
            Click to view details <ChevronRight className="h-3 w-3 ml-1" />
          </div>
        </Card>
      </div>

      {/* Global Login Map */}
      <div className="relative z-0">
        <LoginLocationsMap adminToken={getToken()} />
      </div>

      {/* Drill-down Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">{getDialogTitle()}</DialogTitle>
            <DialogDescription className="font-ibm">
              Detailed view of {getDialogTitle().toLowerCase()}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {renderDialogContent()}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
